import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { operatorApiFetch } from '@services/operator/apiService';
import '../FulfillmentDashboard/FulfillmentWall.css';
import './InboundWall.css';

const POLL_INTERVAL = 60_000;

const SIGNAL_LABELS = {
  green: 'on_track',
  yellow: 'falling_behind',
  red: 'wont_finish',
};

// 從原始數據 + 當前時間計算所有展示指標
const computeMetrics = (data, now) => {
  if (!data) {
    return { total: 0, handled: 0, remaining: 0, completionRate: 0, rate: 0, signal: 'green', remainingHours: 0, projectedFinish: null };
  }

  const handled = data.handled_items || 0;
  const remaining = data.remaining_items || 0;
  const total = handled + remaining;
  const completionRate = total > 0 ? (handled / total) * 100 : 0;
  const rate = data.current_rate || 0;

  // 用 generated_at 做時間基準
  const refTime = data.generated_at ? new Date(data.generated_at) : now;
  const shiftEnd = data.shift_end ? new Date(data.shift_end) : null;
  const remainingHours = shiftEnd ? Math.max((shiftEnd - refTime) / 3_600_000, 0) : 0;

  let signal = 'green';
  let projectedFinish = null;

  if (remaining === 0) {
    signal = 'green';
  } else if (rate > 0) {
    const hoursNeeded = remaining / rate;
    projectedFinish = new Date(now.getTime() + hoursNeeded * 3_600_000);
    if (remainingHours <= 0) {
      signal = 'red';
    } else if (hoursNeeded <= remainingHours - 1) {
      signal = 'green';
    } else if (hoursNeeded <= remainingHours) {
      signal = 'yellow';
    } else {
      signal = 'red';
    }
  } else {
    signal = 'red';
  }

  return { total, handled, remaining, completionRate, rate, signal, remainingHours, projectedFinish };
};

const InboundWall = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [prevRate, setPrevRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [clock, setClock] = useState(new Date());
  const containerRef = useRef(null);
  const wakeLockRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await operatorApiFetch('inbound_dashboard/inbound_progress');
      if (res.ok) {
        const json = await res.json();
        setData((prev) => {
          if (prev) setPrevRate(prev.current_rate);
          return json.data;
        });
        setStale(false);
      } else {
        setStale(true);
      }
    } catch {
      setStale(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchData]);

  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Wake Lock
  useEffect(() => {
    const req = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch { /* ignore */ }
    };
    req();
    const onVis = () => { if (document.visibilityState === 'visible') req(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      wakeLockRef.current?.release();
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  // 每秒隨時鐘重算所有指標
  const m = useMemo(() => computeMetrics(data, clock), [data, clock]);

  const projectedFinishStr = useMemo(() => {
    if (!m.projectedFinish) return '--:--';
    return m.projectedFinish.toLocaleTimeString('zh-TW', {
      hour12: false, hour: '2-digit', minute: '2-digit',
    });
  }, [m.projectedFinish]);

  // 趨勢箭頭
  const trend = useMemo(() => {
    if (prevRate === null || prevRate === m.rate) return 'flat';
    return m.rate > prevRate ? 'up' : 'down';
  }, [m.rate, prevRate]);
  const trendArrow = trend === 'up' ? '\u2191' : trend === 'down' ? '\u2193' : '\u2192';

  // Hourly sparkline data (8:00–18:00)
  const dataHour = useMemo(() => {
    if (data?.generated_at) return new Date(data.generated_at).getHours();
    return clock.getHours();
  }, [data?.generated_at, clock]);
  const currentHour = dataHour;
  const hourlyBars = useMemo(() => {
    const raw = data?.hourly_putaway || [];
    const bars = [];
    for (let h = 8; h <= 18; h++) {
      const found = raw.find((item) => item.hour === h);
      bars.push({ hour: h, count: found ? found.count : 0 });
    }
    return bars;
  }, [data?.hourly_putaway]);

  const maxHourly = useMemo(
    () => Math.max(...hourlyBars.map((b) => b.count), 1),
    [hourlyBars],
  );

  // Ring SVG
  const radius = 148;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (m.completionRate / 100) * circumference;

  // Clock display
  const timeStr = clock.toLocaleTimeString('zh-TW', { hour12: false });
  const dateStr = `${String(clock.getMonth() + 1).padStart(2, '0')}/${String(clock.getDate()).padStart(2, '0')}`;

  if (loading && !data) {
    return (
      <div className="fw-loading">
        <div className="fw-loading-spinner" />
        <div className="fw-loading-text">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="fw iw" ref={containerRef}>
      <div className="fw-header">
        <div className="fw-header-left">
          <span className="fw-header-logo">GoWarehouse</span>
          <span className="fw-header-sep" />
          <span className="fw-header-title">{t('operator.inbound_dashboard.title')}</span>
          <span className="iw-zone-badge">{t('operator.inbound_dashboard.zone')}</span>
        </div>
        <div className="fw-header-right">
          <span className={`fw-live fw-live--${m.signal}`} />
          <span className="fw-clock">{timeStr}</span>
          <span className="fw-date">{dateStr}</span>
        </div>
      </div>

      <div className="fw-main">
        {/* Left: Ring + Stats + Projection */}
        <div className="fw-left">
          <div className="fw-ring-section">
            <div className="fw-ring-container">
              <svg
                className="fw-ring-svg"
                viewBox="0 0 340 340"
                style={{ '--ring-circumference': circumference }}
              >
                <circle className="fw-ring-track" cx="170" cy="170" r={radius} />
                <circle
                  className={`fw-ring-fill fw-ring-fill--${m.signal}`}
                  cx="170"
                  cy="170"
                  r={radius}
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                />
              </svg>
              <div className="fw-ring-center">
                <span className={`fw-ring-pct fw-ring-pct--${m.signal}`}>
                  {Math.round(m.completionRate)}<span className="fw-ring-pct-unit">%</span>
                </span>
                <span className={`fw-ring-signal fw-ring-signal--${m.signal}`}>
                  {t(`operator.inbound_dashboard.signal.${SIGNAL_LABELS[m.signal]}`)}
                </span>
              </div>
            </div>
          </div>

          <div className="fw-stats">
            <div className="fw-stat">
              <span className="fw-stat-value">{m.handled}</span>
              <span className="fw-stat-label">{t('operator.inbound_dashboard.handled')}</span>
            </div>
            <span className="fw-stat-divider" />
            <div className="fw-stat">
              <span className="fw-stat-value">{m.remaining}</span>
              <span className="fw-stat-label">{t('operator.inbound_dashboard.remaining')}</span>
            </div>
            <span className="fw-stat-divider" />
            <div className="fw-stat">
              <span className="fw-stat-value">{m.total}</span>
              <span className="fw-stat-label">{t('operator.inbound_dashboard.total')}</span>
            </div>
          </div>

          <div className="fw-projection">
            <div className="fw-proj-item">
              {t('operator.inbound_dashboard.rate')}
              <strong>{m.rate}</strong>
              {t('operator.inbound_dashboard.per_hour')}
              <span className={`fw-trend-${trend}`}>{trendArrow}</span>
            </div>
            <span className="fw-proj-sep">|</span>
            <div className="fw-proj-item">
              {t('operator.inbound_dashboard.est_finish')}
              <strong>{m.remaining > 0 ? projectedFinishStr : '--:--'}</strong>
            </div>
            <span className="fw-proj-sep">|</span>
            <div className="fw-proj-item">
              {t('operator.inbound_dashboard.shift_remaining')}
              <strong>{m.remainingHours.toFixed(1)}h</strong>
            </div>
          </div>

          <div className="fw-projection">
            <div className="fw-proj-item">
              {t('operator.inbound_dashboard.pending_inbounds')}
              <strong>{data?.pending_inbounds ?? 0}</strong>
            </div>
            <span className="fw-proj-sep">|</span>
            <div className="fw-proj-item">
              {t('operator.inbound_dashboard.processing_inbounds')}
              <strong>{data?.processing_inbounds ?? 0}</strong>
            </div>
            <span className="fw-proj-sep">|</span>
            <div className="fw-proj-item">
              {t('operator.inbound_dashboard.done_inbounds')}
              <strong>{data?.done_inbounds_today ?? 0}</strong>
            </div>
          </div>
        </div>

        {/* Right: Hourly Trend */}
        <div className="fw-right">
          <div className="fw-hourly-title">{t('operator.inbound_dashboard.hourly_title')}</div>
          <div className="fw-hourly">
            {hourlyBars.map((bar) => {
              const pct = (bar.count / maxHourly) * 100;
              const isCurrent = bar.hour === currentHour;
              const isFuture = bar.hour > currentHour;
              return (
                <div key={bar.hour} className={`fw-hourly-col${isCurrent ? ' fw-hourly-col--now' : ''}`}>
                  <span className="fw-hourly-count">{!isFuture && bar.count > 0 ? bar.count : ''}</span>
                  <div className="fw-hourly-bar-track">
                    <div
                      className={`fw-hourly-bar-fill fw-hourly-bar-fill--${m.signal}`}
                      style={{
                        height: isFuture ? '0%' : `${Math.max(pct, bar.count > 0 ? 6 : 0)}%`,
                      }}
                    />
                  </div>
                  <span className={`fw-hourly-label${isCurrent ? ' fw-hourly-label--now' : ''}`}>
                    {bar.hour}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="fw-footer">
        <div className="fw-footer-info">
          <span>
            {t('operator.inbound_dashboard.last_updated')}{' '}
            {data?.generated_at
              ? new Date(data.generated_at).toLocaleTimeString('zh-TW', { hour12: false })
              : '--:--:--'}
          </span>
          <span className="fw-footer-sep">|</span>
          <span>{t('operator.inbound_dashboard.polling_interval', { seconds: POLL_INTERVAL / 1000 })}</span>
          {stale && <span className="fw-stale">{t('operator.inbound_dashboard.data_stale')}</span>}
        </div>
        <button className="fw-fs-btn" onClick={toggleFullscreen}>
          {t('operator.inbound_dashboard.fullscreen')}
        </button>
      </div>
    </div>
  );
};

export default InboundWall;
